import { useEffect, useRef, useState } from "react"
import { useSession } from "../auth/session"
import useMenuBrowser from "../hooks/useMenuBrowser"
import { IMAGE_CACHE_STATE } from "../offline/imageCache"
import { useMenuData } from "../offline/menuData"
import { ACTIVITY_TYPE } from "../network/activity"
import AppHeader from "../components/AppHeader"
import StaleBanner from "../components/StaleBanner"
import { CategoryTabs, MealPeriodTabs } from "../components/MenuTabs"
import AllergenFilter from "../components/AllergenFilter"
import MenuList from "../components/MenuList"
import ItemDetail from "../components/ItemDetail"
import OfflineDataSheet from "../components/OfflineDataSheet"
import Loader from "../components/Loader"
import FirstSyncPanel from "../components/FirstSyncPanel"
import SyncError from "../components/SyncError"
import "./MenuPage.css"

export default function MenuPage({ onSwitchRestaurant, onOpenSearch }) {
  const { user, restaurant, signOut, language, languages, setLanguage } = useSession()
  const { queueActivity, menu: savedMenu, imageProgress } = useMenuData()
  const menu = useMenuBrowser()
  const [selectedItem, setSelectedItem] = useState(null)
  const [isOfflineDataOpen, setIsOfflineDataOpen] = useState(false)
  const listRef = useRef(null)
  const sentinelRef = useRef(null)
  const hasTrackedVisit = useRef(false)
  // Nothing was saved when this screen opened, so the first sync gets a panel of its own.
  const [isFirstRun, setIsFirstRun] = useState(() => !savedMenu)

  const { loadMore, hasMore, isLoading, isLoadingMore } = menu
  const photosSettled =
    imageProgress.state !== IMAGE_CACHE_STATE.IDLE &&
    imageProgress.state !== IMAGE_CACHE_STATE.SAVING
  const showFirstSync = isFirstRun && (isLoading || Boolean(savedMenu))

  useEffect(() => {
    if (isFirstRun && savedMenu && photosSettled) setIsFirstRun(false)
  }, [isFirstRun, savedMenu, photosSettled])

  // One menu_visited per visit to this screen, sent through the outbox.
  useEffect(() => {
    if (hasTrackedVisit.current) return
    hasTrackedVisit.current = true
    queueActivity(ACTIVITY_TYPE.MENU_VISITED)
  }, [queueActivity])

  useInfiniteScroll({
    rootRef: listRef,
    sentinelRef,
    enabled: hasMore && !isLoading && !isLoadingMore,
    onReach: loadMore
  })

  // A new listing starts at the top, as a fresh push would on the native screen.
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 })
  }, [menu.selectedPeriod?.name, menu.selectedCategoryName])

  return (
    <div className="menu-page">
      <AppHeader
        title="Menu"
        onOpenSearch={onOpenSearch}
        onOpenOfflineData={() => setIsOfflineDataOpen(true)}
        restaurant={restaurant}
        user={user}
        languages={languages}
        language={language}
        onChangeLanguage={setLanguage}
        onSwitchRestaurant={onSwitchRestaurant}
        onSignOut={signOut}
      />

      <StaleBanner />

      <MealPeriodTabs
        mealPeriods={menu.mealPeriods}
        selectedName={menu.selectedPeriod?.name ?? ""}
        onSelect={menu.selectPeriod}
      />

      <CategoryTabs
        categories={menu.categories}
        selectedName={menu.selectedCategoryName}
        onSelect={menu.selectCategory}
      />

      {menu.supportsAllergenFilter && (
        <AllergenFilter
          filters={menu.allergenFilters}
          selectedIds={menu.selectedAllergenIds}
          showAllergens={menu.showAllergens}
          onToggle={menu.toggleAllergen}
          onModeChange={menu.setShowAllergens}
        />
      )}

      <div className="menu-page__list-wrap">
        <div className="menu-page__list scroll-y" ref={listRef}>
          {menu.error && !menu.items.length ? (
            <SyncError message={menu.error.message} />
          ) : (
            <>
              <MenuList
                items={menu.items}
                categoryHeader={menu.categoryHeader}
                selectedAllergenIds={menu.selectedAllergenIds}
                showAllergens={menu.showAllergens}
                onSelectItem={setSelectedItem}
              />
              {!isLoading && !menu.items.length && (
                <p className="menu-page__empty t-y24">{menu.emptyMessage}</p>
              )}
              {isLoadingMore && <Loader label="Loading more items" />}
              <div ref={sentinelRef} className="menu-page__sentinel" />
            </>
          )}
        </div>
        {showFirstSync ? (
          <FirstSyncPanel onDismiss={() => setIsFirstRun(false)} />
        ) : (
          isLoading && <Loader overlay />
        )}
      </div>

      {selectedItem && <ItemDetail item={selectedItem} onClose={() => setSelectedItem(null)} />}

      {isOfflineDataOpen && <OfflineDataSheet onClose={() => setIsOfflineDataOpen(false)} />}
    </div>
  )
}

function useInfiniteScroll({ rootRef, sentinelRef, enabled, onReach }) {
  const onReachRef = useRef(onReach)
  onReachRef.current = onReach

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!enabled || !sentinel) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onReachRef.current()
      },
      { root: rootRef.current, rootMargin: "320px" }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [enabled, rootRef, sentinelRef])
}
